import React, { useState } from "react";
import { Link } from "react-router-dom";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { formatSaleDateTime, getSaleThumbnailSrc } from "../utils/saleUtils";
import { PurchasePriceCell } from "./PurchaseCouponMark";

const tableStyle = {
  width: "100%",
  minWidth: "800px",
  borderCollapse: "separate",
  borderSpacing: "0 10px",
  tableLayout: "fixed",
};

const headerCellStyle = {
  border: "none",
  padding: "8px 16px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#6c757d",
  letterSpacing: "0.02em",
  verticalAlign: "bottom",
};

const bodyCellStyle = {
  border: "none",
  padding: "14px 16px",
  verticalAlign: "middle",
  backgroundColor: "#fff",
};

const rowShadowStyle = {
  filter: "drop-shadow(0 2px 8px rgba(99, 99, 99, 0.2))",
};

const getPurchaseListingPath = (productDetails) => {
  const id = productDetails?.id;
  if (id == null || id === "") return null;
  const isBundle =
    Array.isArray(productDetails?.bundle_thumbnails) &&
    productDetails.bundle_thumbnails.length > 0;
  return isBundle ? `/bundle?id=${id}` : `/mockup?id=${id}`;
};

function PurchaseThumbnail({ productDetails, title }) {
  const [hasError, setHasError] = useState(false);
  const src = getSaleThumbnailSrc(productDetails);
  const showPlaceholder = !src || hasError;

  if (showPlaceholder) {
    return (
      <div
        className="rounded-3 d-flex align-items-center justify-content-center"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: "#E9ECEF",
          color: "#ADB5BD",
        }}
        aria-hidden="true"
      >
        <ImageNotSupportedOutlinedIcon fontSize="small" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={title ? `${title} thumbnail` : "Purchase thumbnail"}
      className="rounded-3"
      style={{
        width: "80px",
        height: "80px",
        objectFit: "cover",
        objectPosition: "center",
        display: "block",
      }}
      onError={() => setHasError(true)}
    />
  );
}

export function RecentPurchasesList({ items, emptyMessage = "No purchases yet." }) {
  if (!items?.length) {
    return <p className="text-muted mb-0">{emptyMessage}</p>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "96px" }} />
          <col />
          <col style={{ width: "18%" }} />
          <col style={{ width: "140px" }} />
          <col style={{ width: "180px" }} />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" aria-hidden="true" style={headerCellStyle} />
            <th
              scope="col"
              style={{ ...headerCellStyle, textAlign: "left" }}
            >
              Title
            </th>
            <th
              scope="col"
              style={{ ...headerCellStyle, textAlign: "left" }}
            >
              Seller
            </th>
            <th
              scope="col"
              style={{ ...headerCellStyle, textAlign: "right" }}
            >
              Price
            </th>
            <th
              scope="col"
              style={{ ...headerCellStyle, textAlign: "right" }}
            >
              Purchased
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const title = item?.product_details?.title;
            const seller = item?.product_details?.user_name;
            const price = item?.price ?? item?.product_details?.price;
            const listingPath = getPurchaseListingPath(item?.product_details);

            return (
              <tr key={item?.id ?? index} style={rowShadowStyle}>
                <td
                  style={{
                    ...bodyCellStyle,
                    borderRadius: "16px 0 0 16px",
                    paddingLeft: "20px",
                    width: "96px",
                  }}
                >
                  {listingPath ? (
                    <Link to={listingPath} className="d-inline-block">
                      <PurchaseThumbnail
                        productDetails={item?.product_details}
                        title={title}
                      />
                    </Link>
                  ) : (
                    <PurchaseThumbnail
                      productDetails={item?.product_details}
                      title={title}
                    />
                  )}
                </td>
                <td style={bodyCellStyle}>
                  {listingPath ? (
                    <Link
                      to={listingPath}
                      className="mb-0 fw-semibold text-truncate d-block text-decoration-none text-dark"
                      style={{ fontSize: "14px" }}
                      title={title}
                    >
                      {title || "—"}
                    </Link>
                  ) : (
                    <p
                      className="mb-0 fw-semibold text-truncate"
                      style={{ fontSize: "14px" }}
                      title={title}
                    >
                      {title || "—"}
                    </p>
                  )}
                </td>
                <td style={bodyCellStyle}>
                  <p
                    className="mb-0 text-truncate"
                    style={{ fontSize: "13px" }}
                    title={seller}
                  >
                    {seller || "—"}
                  </p>
                </td>
                <td style={{ ...bodyCellStyle, textAlign: "right" }}>
                  <PurchasePriceCell
                    price={price}
                    coupon={item?.coupon}
                    isRefunded={item?.is_refunded}
                  />
                </td>
                <td
                  style={{
                    ...bodyCellStyle,
                    borderRadius: "0 16px 16px 0",
                    textAlign: "right",
                    paddingRight: "20px",
                  }}
                >
                  <p className="purchase-date">
                    {formatSaleDateTime(item?.created_at)}
                  </p>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default RecentPurchasesList;
